import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, ArrowDownToLine, PackageOpen, Truck, Store, Package, Timer, Trophy, Gauge, Zap, AlertTriangle, X, ScanBarcode, PackageX, Hourglass, Users } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { playSuccess, playError, playComplete } from "./helpers/audioPlayer";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { useDemoVisible } from "./helpers/demoMode";
import { recordItemSorted } from "./helpers/sessionItems";
import { preventScanPageButtonFocus, blurClickedScanPageButton } from "./helpers/scanFocusGuard";
import HiddenScanInput from "./HiddenScanInput";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import ZoneSorterLocationBoxes from "./zone-sorter-location-boxes";
import PickAndSortUnloadGrid, { type PickAndSortBaySlot } from "./PickAndSortUnloadGrid";
import {
    useProgressTimer,
    useStreak,
    useBestTime,
    useSessionScore,
    ProgressBar,
    TimeBonusPopup,
    AchievementPopup,
    ScoreDisplay,
} from "./gamification";
import { apiFetch } from "../api/apiFetch";
import { recordScanAnomaly } from "../api/scanTelemetry";
import "../styles/scansheet.css";
import "../styles/zonestorage.css";
import "../styles/picking.css";
import "../styles/add-user.css";

// Falls back until the warehouse's pick_and_sort_timer_ms loads (Settings >
// Sorting > Pick & Sort > Location Time Targets). The timer measures one full
// sort cycle: item scan → place → location scan → next item scan.
const TIMER_DURATION_MS = 6000;

// Unloader slot list shows this many up front; the rest sit behind "Load more".
const SLOT_PREVIEW_COUNT = 5;

// Compact wave duration for the completion summary. Steps up a unit once it
// rolls over: "47s" → "15m 5s" → "5h 3m" (seconds dropped once into hours).
const formatDuration = (ms: number): string => {
    const totalSec = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
};

// Items-per-hour for the completion summary, extrapolated from this wave's own
// run. Needs both a positive elapsed time and at least one item to mean
// anything — a resumed wave (no elapsed time banked) or a wave where every unit
// was flagged reads "—" rather than a fabricated or infinite rate.
const formatHourlyRate = (items: number, elapsedMs: number): string => {
    if (items <= 0 || elapsedMs <= 0) return "—";
    return String(Math.round(items / (elapsedMs / 3600000)));
};

export type PickAndSortAllocation = {
    allocation_id: number;
    order_id: string;
    order_number: string;
    delivery_type: string | null;
    location_barcode: string;
    label: string;
    shelf: string;
    position: number;
    shelf_colour: string | null;
    position_colour: string | null;
    priority: number;
    large_priority?: number | null;
    bay_id?: number | null;
    bay_name?: string | null;
    items_required: number;
    items_sorted: number;
    status: string;
    // Two-cubby Putwall orders (BE migration 213). Absent on an older BE and
    // null / 1 / 0 on an ordinary one-cubby order. items_sorted_2 is what
    // sits in cubby 2 (cubby 1 holds the rest); tote_id / tote_id_2 are set
    // once that cubby has been unloaded into a dispatch tote.
    active_slot?: 1 | 2;
    items_sorted_2?: number;
    tote_id?: string | null;
    tote_id_2?: string | null;
    location_2?: {
        location_barcode: string; label: string; shelf: string; position: number;
        shelf_colour: string | null; position_colour: string | null;
    } | null;
};

// One cubby the closer can empty right now. Unloader rows are CUBBIES, not
// orders: a two-cubby Putwall order lists each cubby that still holds stock
// and has no tote yet, and each goes out in its own tote.
export type UnloadRow = {
    key: string;
    allocation_id: number;
    order_id: string;
    order_number: string;
    delivery_type: string | null;
    bay_id?: number | null;
    bay_name?: string | null;
    location_barcode: string;
    label: string;
    items: number;
    cubby: 1 | 2;
    two_cubby: boolean;
};

export const cubbyRows = (list: PickAndSortAllocation[]): UnloadRow[] => {
    const rows: UnloadRow[] = [];
    for (const a of list) {
        if (a.status !== "sorted") continue;
        const two = !!a.location_2;
        const s2 = Number(a.items_sorted_2) || 0;
        const s1 = two ? a.items_sorted - s2 : a.items_sorted;
        const base = {
            allocation_id: a.allocation_id, order_id: a.order_id, order_number: a.order_number,
            delivery_type: a.delivery_type, bay_id: a.bay_id, bay_name: a.bay_name, two_cubby: two,
        };
        if (!a.tote_id && (!two || s1 > 0)) {
            rows.push({ ...base, key: `${a.allocation_id}:1`, location_barcode: a.location_barcode, label: a.label, items: s1, cubby: 1 });
        }
        if (two && a.location_2 && !a.tote_id_2 && s2 > 0) {
            rows.push({ ...base, key: `${a.allocation_id}:2`, location_barcode: a.location_2.location_barcode, label: a.location_2.label, items: s2, cubby: 2 });
        }
    }
    return rows;
};

// One bay of the wave's plan — a Large (double-bay) wave carries two.
export type PickAndSortBayPlan = {
    bay_id: number;
    name: string | null;
    position: number | null;
    slots: PickAndSortBaySlot[];
};

// A row of GET /pick-and-sort/bay-ready — one live P&S / put-wall bay in the
// warehouse and how many sorted orders are sitting in it ready to go out to a
// dispatch tote. Backs the closer's per-bay table in the unloading view.
export type PickAndSortBayReady = {
    bay_id: number;
    name: string | null;
    station: string;
    ready_orders: number;
    ready_items: number;
    sorting_orders: number;
};

export type PickAndSortSegments = {
    total: number;
    completed: number;
    picking: boolean;
    members: Array<{
        wave_id: number;
        segment_no: number;
        status: string;
        awaiting_picker: boolean;
        picker_name: string | null;
        lines_total: number;
        lines_done: number;
        items_total: number;
        items_picked: number;
    }>;
};

export type PickAndSortSession = {
    wave_id: number;
    wave_size?: "small" | "large";
    // Where the wave is sorted: a Pick & Sort bay (default) or the put
    // wall. Optional — an older BE omits it. Only labels the header here.
    sort_station?: "pick_and_sort" | "put_wall";
    bay_id: number | null;
    warehouse: string;
    total_required: number;
    total_sorted: number;
    order_count?: number;
    source_totes?: string[];
    // Paired wave (migration 223): the halves and where each pick stands.
    // group_picking true = a half is still on the floor: its units are in
    // total_required but cannot be written off yet. Absent on an older
    // BE or a solo wave.
    segments?: PickAndSortSegments | null;
    group_picking?: boolean;
    // True when the session was entered by scanning a source tote (the
    // tote-override path) — the tote gate is already satisfied.
    tote_confirmed?: boolean;
    // Set when the bay scan RESUMED a wave already mid-sort rather than
    // claiming a new one, with is_wave_sorter saying whether the scanner is
    // the operator who claimed it. A second operator (is_wave_sorter false)
    // is offered the job picker: keep sorting, or start emptying the slots
    // that are already finished while the sort carries on.
    resumed?: boolean;
    is_wave_sorter?: boolean;
    sorter_name?: string | null;
    bay_slots?: PickAndSortBaySlot[];
    bays?: PickAndSortBayPlan[];
    allocations: PickAndSortAllocation[];
};

type SortView = "loading" | "unloading";

// A row of GET /:waveId/unsorted-items — per-SKU outstanding demand. Backs
// both item lists in the flag modal (manual sort and missing).
type UnsortedItem = {
    sku: string;
    title: string | null;
    barcode: string | null;
    image: string | null;
    remaining: number;
};

// Closer's per-bay table — one line per bay in the warehouse with the number
// of orders ready to close (sorted, waiting for a dispatch tote). Pembroke's
// closer covers several bays with one device, and that device only knows the
// wave at the bay it scanned; this is how they see where they're needed next
// without walking the aisle. Hidden for a single-bay warehouse, where the
// one row would just repeat the count already on screen. The bay the device
// is working is marked so the table also answers "where am I". Exported:
// the Scan Location gate (zone-storage-select-location) shows the same table
// under its own Unloading pill, before any wave is open.
export const PasBayReadyTable = ({ bays, currentBayIds }: { bays: PickAndSortBayReady[]; currentBayIds: Set<number> }) => {
    const { t } = useTranslation();
    if (bays.length < 2) return null;
    return (
        <div className="pas-bay-ready" role="table" aria-label={t("pas_bay_ready_title")}>
            <div className="pas-bay-ready-title">{t("pas_bay_ready_title")}</div>
            {bays.map(b => {
                const here = currentBayIds.has(b.bay_id);
                const name = b.name ?? `#${b.bay_id}`;
                return (
                    <div
                        key={b.bay_id}
                        role="row"
                        className={`pas-bay-ready-row${here ? " pas-bay-ready-row--here" : ""}${b.ready_orders > 0 ? " pas-bay-ready-row--has" : ""}`}
                        aria-label={t("pas_bay_ready_row_aria", { bay: name, count: String(b.ready_orders) })}
                    >
                        <span role="cell" className="pas-bay-ready-name">
                            {name}
                            {here && <span className="pas-bay-ready-here">{t("pas_bay_ready_here")}</span>}
                        </span>
                        <span role="cell" className="pas-bay-ready-count">{b.ready_orders}</span>
                    </div>
                );
            })}
        </div>
    );
};

const ZoneStoragePickAndSort = ({ session, resetFunc, onWaveChange }: { session: PickAndSortSession; resetFunc: () => void; onWaveChange?: (s: PickAndSortSession) => void }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const demo = useDemoVisible();

    // A SECOND operator has scanned into a bay someone else is already sorting.
    // They're here to help, and the useful help is emptying the slots that have
    // already finished — an order is unloadable the moment its last item is
    // sorted, so the bay can be emptied order-by-order while the wave is still
    // being sorted. Ask which job they're doing rather than dropping them onto
    // a live sorting screen where their next scan sorts an item.
    const joinedOthersWave = session.resumed === true && session.is_wave_sorter === false;
    const sortingLeft = session.total_required > session.total_sorted;
    // Nothing left to sort means there's no job to pick between — the only work
    // at this bay is emptying it, so skip the picker and open on the unloader.
    const [rolePick, setRolePick] = useState(joinedOthersWave && sortingLeft);
    // Unload-only: this device empties slots and nothing else. The loading view
    // is not reachable from here, so a stray item scan can't sort anything into
    // the wave the other operator is working. One scan back through the bay
    // barcode is all it takes to swap roles.
    const [unloadOnly, setUnloadOnly] = useState(joinedOthersWave && !sortingLeft);
    const [view, setView] = useState<SortView>(joinedOthersWave && !sortingLeft ? "unloading" : "loading");
    const [scan, setScan] = useState("");
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [loading, setLoading] = useState(false);
    // total_required can move on a paired wave: the other half's units are
    // counted as "coming" at entry and replaced by what was actually picked
    // when that half lands (topUpGroupAllocations) — the plan poll carries it.
    const [totalRequired, setTotalRequired] = useState(session.total_required);
    const [totalSorted, setTotalSorted] = useState(session.total_sorted);
    const [groupPicking, setGroupPicking] = useState(session.group_picking === true);
    const [segments, setSegments] = useState<PickAndSortSegments | null>(session.segments ?? null);
    const [allSorted, setAllSorted] = useState(session.total_required > 0 && session.total_sorted >= session.total_required && session.group_picking !== true);
    // Before sorting starts, the operator confirms the right consolidated tote is
    // at the bay by scanning it. Skip the gate when resuming a wave that's already
    // mid-sort (or finished) — they've clearly already got the tote — and when the
    // session was entered BY a tote scan (the switch-wave path), which is itself
    // the confirmation.
    const sourceTotes = useMemo(() => session.source_totes ?? [], [session.source_totes]);
    const [toteConfirmed, setToteConfirmed] = useState(
        session.total_sorted > 0 || sourceTotes.length === 0 || session.tote_confirmed === true,
    );
    // `partner` is the order's OTHER cubby on a two-cubby Putwall order: the
    // sorter may put the unit there when the shown cubby is full and scan
    // IT to confirm — that swap routes the rest of the order to it.
    const [placement, setPlacement] = useState<{ shelf: string; position: number | string; shelfColour: string | null; positionColour: string | null; label: string; barcode: string; bayName: string | null; partner: { label: string; barcode: string } | null } | null>(null);
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    // Set when the LAST item of the wave has been scanned but its placement
    // hasn't been confirmed yet. Placement is mandatory for every item — the
    // wave only reads complete once the operator scans the final slot's own
    // barcode (handleConfirmLocation), never straight off the item scan.
    // Carries the summary item count so the confirm can build waveSummary.
    const pendingCompleteRef = useRef<{ items: number } | null>(null);
    // Mirrors `placement` for code that runs outside the render cycle
    // (refreshPlan's poll/response handlers): "is a slot card up right now?".
    // Set synchronously where the card goes up so a plan response can never
    // slip in between the scan response and the effect below.
    const placementRef = useRef<boolean>(false);
    useEffect(() => { placementRef.current = placement !== null; }, [placement]);

    // Gamification — identical stack to Sorter 1. The progress timer measures one
    // full sort cycle: it (re)starts on each item scan and runs until the NEXT
    // item is scanned — covering placement AND the walk back for the next item.
    // Scoring is awarded for the cycle that just ended, when the next item scans.
    const timer = useProgressTimer();
    const streakApi = useStreak();
    const bestTime = useBestTime("pas-best-time");
    const score = useSessionScore();
    const timerDurationRef = useRef(TIMER_DURATION_MS);

    const [timeBonus, setTimeBonus] = useState<number | null>(null);
    const [timeBonusColor, setTimeBonusColor] = useState<"green" | "amber">("green");
    const [isBestTime, setIsBestTime] = useState<boolean>(false);
    const timeBonusRef = useRef<NodeJS.Timeout | null>(null);
    const timeBonusKeyRef = useRef<number>(0);

    // Achievements — fired in the same overlay slot as the time bonus when the
    // operator crosses a 100-item milestone (tallied daily, across waves).
    const [achievement, setAchievement] = useState<number | null>(null);
    const achievementKeyRef = useRef<number>(0);
    const achievementTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Per-wave run stats for the completion summary. waveStart stamps the first
    // item scan of this mount; wavePoints accumulates the time-bonus seconds
    // awarded as the wave is sorted. Both reset naturally on remount (a new
    // wave swaps the session → fresh component).
    const waveStartRef = useRef<number | null>(null);
    const wavePointsRef = useRef<number>(0);
    // `flagged` is set only on the flag-remaining finish, so the summary can
    // say why the item count came up short of the wave's demand.
    const [waveSummary, setWaveSummary] = useState<{ items: number; elapsedMs: number; points: number; flagged?: number } | null>(null);

    // Build the completion summary from whatever this session actually banked.
    // Every path that ends a wave calls this — a clean final placement AND a
    // flag-remaining finish — so the operator always sees their run stats. The
    // item count is passed in because the two paths know it differently: the
    // clean finish carries it on pendingCompleteRef, while flagging reads the
    // live sorted tally (the flagged units were never sorted, so they don't
    // count towards items or the rate).
    //
    // Only when this operator actually sorted something this session, though —
    // waveStartRef stamps their first scan, so a null start means they resumed
    // someone else's wave purely to finish or flag it and have no run of their
    // own to report (showing one would credit them with the other operator's
    // items over a zero elapsed time).
    const buildWaveSummary = useCallback((items: number, flagged = 0) => {
        if (waveStartRef.current === null) return;
        setWaveSummary({
            items,
            elapsedMs: Date.now() - waveStartRef.current,
            points: wavePointsRef.current,
            flagged,
        });
    }, []);

    useEffect(() => () => {
        if (achievementTimerRef.current) clearTimeout(achievementTimerRef.current);
        if (timeBonusRef.current) clearTimeout(timeBonusRef.current);
    }, []);

    useEffect(() => {
        if (!session.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/settings/${encodeURIComponent(session.warehouse)}`, {});
                if (!res.ok) return;
                const data = await res.json();
                if (!alive) return;
                if (typeof data.pick_and_sort_timer_ms === "number" && data.pick_and_sort_timer_ms > 0) {
                    timerDurationRef.current = data.pick_and_sort_timer_ms;
                }
            } catch {
                // leave default
            }
        })();
        return () => { alive = false; };
    }, [session.warehouse]);

    // Unloading state — fresh allocation list (statuses go stale once sorting
    // progresses) plus the two-step scan: slot, then dispatch tote.
    const [allocations, setAllocations] = useState<PickAndSortAllocation[]>(session.allocations);
    // The bay's configured slot layout — drives the unloader grid. Static for the
    // bay, but refreshed alongside the plan so a re-grid mid-wave is picked up.
    const [baySlots, setBaySlots] = useState<PickAndSortBaySlot[]>(session.bay_slots ?? []);
    // Per-bay plans — a Large (double-bay) wave carries two entries and the
    // unloader renders one grid per bay. Small waves carry one (or none on
    // stale sessions, which fall back to the flat baySlots).
    const [bayPlans, setBayPlans] = useState<PickAndSortBayPlan[]>(session.bays ?? []);
    const isLargeWave = (session.wave_size ?? "small") === "large";
    const isPutWall = session.sort_station === "put_wall";
    const [unloadStep, setUnloadStep] = useState<"slot" | "tote">("slot");
    // `cubby` names which of a two-cubby order's cubbies this is (null for a
    // one-cubby order); `auto` = the BE sent us here after the order's first
    // cubby went into a tote, so the closer needn't scan the second cubby.
    const [unloadSlot, setUnloadSlot] = useState<{ location_barcode: string; label: string; order_number: string; delivery_type: string | null; bay_name: string | null; cubby: 1 | 2 | null; auto?: boolean } | null>(null);
    const [unloadedMsg, setUnloadedMsg] = useState<string | null>(null);
    // The slot list previews the first few; "Load more" reveals the rest.
    const [showAllSlots, setShowAllSlots] = useState(false);

    // Flag-remaining-missing escape hatch: when items that were picked never
    // turn up at the bay, the operator opens this modal to write the shortfall
    // off as missing rather than being stuck one scan short forever.
    const [flagModalOpen, setFlagModalOpen] = useState(false);
    const [flagging, setFlagging] = useState(false);
    // Other views inside the flag modal:
    //   manual          — the item is HERE but its barcode won't scan; tap it
    //                     in the unsorted-items list to sort it manually.
    //   missing         — the item ISN'T here; tap it to write it off. Same
    //                     list, opposite meaning, so the tap can't be the
    //                     action: it only chooses what the confirm is about.
    //   missing-confirm — "are you sure?" for exactly that item.
    const [flagView, setFlagView] = useState<"flag" | "manual" | "missing" | "missing-confirm">("flag");
    const [manualItems, setManualItems] = useState<UnsortedItem[] | null>(null);
    const [manualBusy, setManualBusy] = useState(false);
    // The item picked out of the missing list, held while its confirm is up.
    const [missingTarget, setMissingTarget] = useState<UnsortedItem | null>(null);
    // Units written off item-by-item this session, so a wave finished by the
    // last such flag reports its shortfall on the summary the way a
    // flag-remaining finish does.
    const flaggedUnitsRef = useRef(0);

    // Demo scan affordances — barcodes to drive the flow without a scanner.
    const [demoItems, setDemoItems] = useState<string[]>([]);
    const [demoTotes, setDemoTotes] = useState<string[]>([]);

    // Every P&S wave awaiting sort (incl. the one just entered) — shown on the
    // entry gate so the operator can see what's queued: when each was picked, by
    // whom, and how many orders it holds.
    const [waves, setWaves] = useState<{ wave_id: number; completed_at: string | null; picker_name: string | null; order_count: number; item_count?: number; totes?: string[]; in_progress: boolean; wave_size?: "small" | "large"; sort_station?: "pick_and_sort" | "put_wall" }[]>([]);

    // Errors flash red briefly. Success has no visual effect — it returns to
    // idle immediately (just the sound) so sorting doesn't wait on an animation.
    const flashError = () => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus("error");
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), 500);
    };
    const clearStatus = () => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus("idle");
    };

    // Pull a fresh order→slot plan for THIS wave so the loader/unloader counts
    // stay accurate as sorting progresses. Wave-scoped (not /active, which would
    // return the warehouse's oldest active wave) so a second bay/operator sorting
    // a different wave can't clobber this station's view — and so items sorted /
    // slots unloaded by anyone else on the same wave show up here.
    // Returns the fresh allocations (null if the read failed or the wave is no
    // longer active) so a caller that must not act on stale statuses — the slot
    // scan, which another operator's sorting can change under it — can re-check
    // against what the server just said instead of local state.
    const refreshPlan = useCallback(async (): Promise<PickAndSortAllocation[] | null> => {
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/plan`, {});
            if (!res.ok) return null;
            const data = await res.json();
            if (data.active && Array.isArray(data.allocations)) {
                setAllocations(data.allocations);
                if (Array.isArray(data.bay_slots)) setBaySlots(data.bay_slots);
                if (Array.isArray(data.bays)) setBayPlans(data.bays);
                setTotalSorted(data.total_sorted ?? 0);
                if (typeof data.total_required === "number") setTotalRequired(data.total_required);
                const stillPicking = data.group_picking === true;
                setGroupPicking(stillPicking);
                setSegments(data.segments && typeof data.segments === "object" ? data.segments as PickAndSortSegments : null);
                // Don't flip to the complete screen while the final item's
                // placement confirm is still pending — the DB already reads
                // fully sorted at that point, but the operator must scan the
                // last slot's barcode first (handleConfirmLocation finishes).
                // Nor while a half of a paired wave is still being picked:
                // its units are yet to arrive.
                if (data.total_required > 0 && data.total_sorted >= data.total_required
                    && !stillPicking
                    && !pendingCompleteRef.current) {
                    if (placementRef.current) {
                        // A slot card is up, so the wave finished under the
                        // operator: either the scan-item response disagreed
                        // with the plan about completeness (the BE once
                        // counted unloaded slots as still open — Lel's D3
                        // item, 2026-09-24), or another operator sorted the
                        // real last item. Either way the operator is holding
                        // an item that still has to be placed: arm the gate
                        // instead of ending the sort on them, and let the
                        // slot confirm finish the wave like any clean final
                        // placement.
                        pendingCompleteRef.current = { items: data.total_sorted ?? 0 };
                    } else setAllSorted(true);
                }
                return data.allocations as PickAndSortAllocation[];
            }
            return null;
        } catch { /* keep last-known plan */ return null; }
    }, [session.wave_id]);

    // Once the current wave is fully sorted, check whether more P&S waves are
    // still queued for sorting so the complete screen can flag there's more to
    // do rather than reading as "everything's done". null = not yet known.
    const [queueWaiting, setQueueWaiting] = useState<number | null>(null);
    useEffect(() => {
        if (!allSorted || !session.warehouse) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/pick-and-sort/queue-count?warehouse=${encodeURIComponent(session.warehouse)}`, {});
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) setQueueWaiting(typeof data.count === "number" ? data.count : null);
            } catch { /* leave unknown — complete screen just won't show the hint */ }
        })();
        return () => { cancelled = true; };
    }, [allSorted, session.warehouse]);

    // Demo: outstanding item barcodes for the loading view.
    const loadDemoItems = useCallback(async () => {
        if (!demo) return;
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/demo-items`, {});
            if (!res.ok) return;
            const data = await res.json();
            setDemoItems(Array.isArray(data.items) ? data.items : []);
        } catch { setDemoItems([]); }
    }, [demo, session.wave_id]);

    // Demo: available dispatch totes for the unloading view.
    const loadDemoTotes = useCallback(async () => {
        if (!demo) return;
        try {
            const res = await apiFetch(`/pick-and-sort/demo?warehouse=${encodeURIComponent(session.warehouse)}`, {});
            if (!res.ok) return;
            const data = await res.json();
            setDemoTotes(Array.isArray(data.totes) ? data.totes : []);
        } catch { setDemoTotes([]); }
    }, [demo, session.warehouse]);

    useEffect(() => { loadDemoItems(); }, [loadDemoItems]);
    useEffect(() => {
        if (view === "unloading") { refreshPlan(); loadDemoTotes(); }
    }, [view, refreshPlan, loadDemoTotes]);

    // Keep the loader/unloader badge counts honest while OTHER operators sort or
    // unload the same wave (two sorters at one bay, or one sorting while another
    // unloads). Poll this wave's canonical plan every 5s — the same cadence the
    // home tiles poll at. The counts are monotonic (items_sorted only grows; a
    // slot leaves the unloader only once it's dispatched), so a poll can never
    // flash a spurious regression. Cleared on unmount — the next wave remounts.
    useEffect(() => {
        const id = setInterval(() => { refreshPlan(); }, 5000);
        return () => clearInterval(id);
    }, [refreshPlan]);

    // Warehouse-wide "ready to close" per bay, for the closer's table in the
    // unloading view. Fetched on entering that view and then on the same 5s
    // cadence as the plan — the other bays' counts move under this device as
    // their sorters finish orders, and a closer deciding where to go next
    // must not be reading a stale table. Empty (table hidden) until the
    // first read lands or if the BE predates the endpoint.
    const [bayReady, setBayReady] = useState<PickAndSortBayReady[]>([]);
    useEffect(() => {
        if (view !== "unloading" || !session.warehouse) return;
        let cancelled = false;
        const load = async () => {
            try {
                const res = await apiFetch(`/pick-and-sort/bay-ready?warehouse=${encodeURIComponent(session.warehouse)}`, {});
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled && Array.isArray(data.bays)) setBayReady(data.bays);
            } catch { /* keep the last table */ }
        };
        load();
        const id = setInterval(load, 5000);
        return () => { cancelled = true; clearInterval(id); };
    }, [view, session.warehouse]);

    // Load the waiting-waves list while the entry gate is showing. Scoped to
    // this wave's wall: a put-wall sorter only ever gets put-wall waves (and
    // vice versa), so the other wall's queue is noise here.
    useEffect(() => {
        if (toteConfirmed || allSorted || !session.warehouse) return;
        const station = isPutWall ? "put_wall" : "pick_and_sort";
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/pick-and-sort/waves?warehouse=${encodeURIComponent(session.warehouse)}&station=${station}`, {});
                if (!res.ok) return;
                const data = await res.json();
                const list: { sort_station?: string }[] = Array.isArray(data.waves) ? data.waves : [];
                if (!cancelled) setWaves(list.filter(w => (w.sort_station ?? "pick_and_sort") === station) as typeof waves);
            } catch { /* gate still works without the list */ }
        })();
        return () => { cancelled = true; };
    }, [toteConfirmed, allSorted, session.warehouse, isPutWall]);

    // Score one completed full cycle (item scan → next item scan): award the
    // time bonus + streak if finished within the window, reset the streak if it
    // ran over. No-op when no cycle was running (e.g. the very first item).
    const awardCycle = useCallback(() => {
        const { elapsedMs, completed, durationMs } = timer.stop();
        if (durationMs <= 0) return;
        if (!completed) {
            const secondsLeft = Math.ceil((durationMs - elapsedMs) / 1000);
            setTimeBonusColor(elapsedMs / durationMs < 0.5 ? "green" : "amber");

            const { isNewBest, prevExisted } = bestTime.check(elapsedMs);
            setIsBestTime(isNewBest && prevExisted);

            streakApi.increment();
            score.addSeconds(secondsLeft, "pick_and_sort");
            wavePointsRef.current += secondsLeft;

            timeBonusKeyRef.current += 1;
            setTimeBonus(secondsLeft);
            if (timeBonusRef.current) clearTimeout(timeBonusRef.current);
            timeBonusRef.current = setTimeout(() => {
                setTimeBonus(null);
                setIsBestTime(false);
            }, 1500);
        } else {
            setTimeBonus(null);
            setIsBestTime(false);
            streakApi.reset();
        }
    }, [timer, bestTime, streakApi, score]);

    // Shared success path for a sorted unit — used by the barcode scan AND the
    // manual tap-to-sort fallback (flag modal → "barcode not scanning"), which
    // must behave exactly like a scan from here on: placement card up, counters
    // bumped, and the slot confirm still mandatory.
    const applySortSuccess = useCallback((data: {
        wave_sorted: number; wave_required?: number; wave_complete: boolean; allocation_status: string;
        location: { shelf: string; position: number | string; shelf_colour: string | null; position_colour: string | null; label: string; barcode: string; bay_name?: string | null };
        location_2?: { label: string; barcode: string } | null;
    }) => {
        clearStatus();
        playSuccess();
        // This item ends the previous full cycle — score it.
        awardCycle();
        if (waveStartRef.current === null) waveStartRef.current = Date.now();
        // Tally the item against today's running count; a 100-boundary
        // crossing fires an achievement in the time-bonus overlay slot.
        const { milestone } = recordItemSorted();
        if (milestone !== null) {
            achievementKeyRef.current += 1;
            setAchievement(milestone);
            // The achievement owns the overlay slot for this scan — drop
            // any +Ns bonus that would otherwise stack on top of it.
            setTimeBonus(null);
            setIsBestTime(false);
            if (achievementTimerRef.current) clearTimeout(achievementTimerRef.current);
            achievementTimerRef.current = setTimeout(() => setAchievement(null), 2200);
        }
        placementRef.current = true;
        setPlacement({
            shelf: data.location.shelf, position: data.location.position,
            shelfColour: data.location.shelf_colour, positionColour: data.location.position_colour,
            label: data.location.label, barcode: data.location.barcode,
            bayName: data.location.bay_name ?? null,
            partner: data.location_2 ? { label: data.location_2.label, barcode: data.location_2.barcode } : null,
        });
        setTotalSorted(data.wave_sorted);
        // An order just completed → its slot is unloadable from this moment.
        // Mark it locally rather than waiting on the refresh: the unloader
        // badge and slot list are derived from `allocations`, and on the
        // wave's LAST item the operator goes straight to unloading — a list
        // that's one fetch behind is what made that hand-off feel broken.
        // The refresh still runs and remains the source of truth.
        if (data.allocation_status === "sorted") {
            setAllocations(prev => prev.map(a => (
                a.location_barcode === data.location.barcode || a.location_2?.location_barcode === data.location.barcode
                    ? { ...a, status: "sorted", items_sorted: a.items_required }
                    : a
            )));
            refreshPlan();
        }
        // "Last item" is read two ways and either suffices: the BE's flag,
        // or the wave's own counts (sorted >= required) — the same test the
        // plan endpoint applies. The counts are the safety net: if the flag
        // is ever false when the counts say done, the plan refresh would
        // otherwise show the complete screen before the slot is scanned.
        const waveComplete = data.wave_complete
            || (typeof data.wave_required === "number" && data.wave_required > 0
                && data.wave_sorted >= data.wave_required);
        if (waveComplete) {
            // Last item — but the wave is NOT shown complete yet. The
            // placement card stays up and the operator must scan this
            // slot's own barcode like every other item; the confirm
            // (handleConfirmLocation) plays the completion cue, builds
            // the summary and unlocks the complete screen.
            pendingCompleteRef.current = { items: data.wave_sorted };
        } else {
            // Begin the next full cycle — it runs until the next item is
            // scanned (awardCycle above scores it then).
            timer.start(timerDurationRef.current);
        }
        loadDemoItems();
    }, [awardCycle, timer, refreshPlan, loadDemoItems]);

    // ── Loading: scan an item, route it to a slot ───────────────────────────
    const handleSortScan = useCallback(async (val: string) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/scan-item`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: val }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json();
            if (res.ok && data.ok) {
                applySortSuccess(data);
            } else {
                flashError();
                playError();
                toast.dismiss();
                toast.error(data.reason === "no_demand" ? t("pas_no_demand") : t("scan_failed"));
            }
        } catch {
            flashError();
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
        } finally {
            setLoading(false);
        }
    }, [session.wave_id, navigate, t, applySortSuccess]);

    // ── Unloading: scan a sorted slot, then the dispatch tote ────────────────
    const handleSlotScan = useCallback(async (val: string) => {
        let list = allocations;
        let match = cubbyRows(list).find(r => r.location_barcode === val);
        if (!match) {
            // Don't refuse on local state alone. While someone else is sorting
            // the same wave this list is up to 5s behind them, so the slot the
            // unloader is standing at may have been finished seconds ago — and
            // "not finished yet" to an operator looking at a full slot reads as
            // a broken scanner. Re-read the plan and judge on that.
            setLoading(true);
            try {
                const fresh = await refreshPlan();
                if (fresh) {
                    list = fresh;
                    match = cubbyRows(fresh).find(r => r.location_barcode === val);
                }
            } finally { setLoading(false); }
        }
        if (!match) {
            // This refusal is decided entirely client-side — no request is made,
            // so nothing about it reaches the server. Report it, and say WHICH
            // of the three cases it is: "not finished" and "already unloaded"
            // need opposite responses from the operator, and one generic toast
            // made a refused slot look like a dead scanner (wave 5335, C2).
            // A two-cubby order's cubby counts as unloaded once ITS tote is bound.
            const atSlot = list.find(a => a.location_barcode === val || a.location_2?.location_barcode === val);
            const cubbyDone = atSlot ? (atSlot.location_barcode === val ? !!atSlot.tote_id : !!atSlot.tote_id_2) : false;
            const reason = !atSlot ? "slot_unknown"
                : (cubbyDone || atSlot.status === "dispatch_ready" || atSlot.status === "dispatched") ? "slot_already_unloaded"
                    : "slot_not_finished";
            recordScanAnomaly("slot_not_ready", "PickAndSort", {
                reason,
                slot_status: atSlot?.status ?? null,
                sorted: atSlot?.items_sorted ?? null,
                required: atSlot?.items_required ?? null,
            }, { barcode: val });
            flashError();
            playError();
            toast.dismiss();
            toast.error(
                reason === "slot_already_unloaded" ? t("pas_slot_already_unloaded")
                    : reason === "slot_not_finished"
                        ? t("pas_slot_not_finished")
                            .replace("{sorted}", String(atSlot?.items_sorted ?? "?"))
                            .replace("{required}", String(atSlot?.items_required ?? "?"))
                        : t("pas_slot_not_ready"),
            );
            // No refreshPlan here — the re-read above already ran.
            return;
        }
        clearStatus();
        playSuccess();
        setUnloadSlot({
            location_barcode: match.location_barcode, label: match.label, order_number: match.order_number,
            delivery_type: match.delivery_type, bay_name: match.bay_name ?? null,
            cubby: match.two_cubby ? match.cubby : null,
        });
        setUnloadStep("tote");
    }, [allocations, t, refreshPlan]);

    const handleToteScan = useCallback(async (toteBarcode: string) => {
        if (!unloadSlot) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/unload`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ location_barcode: unloadSlot.location_barcode, tote_barcode: toteBarcode }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json();
            if (res.ok && data.ok) {
                clearStatus();
                setUnloadedMsg(`#${unloadSlot.order_number} → ${toteBarcode}`);
                if (data.wave_unloaded) {
                    setUnloadSlot(null);
                    setUnloadStep("slot");
                    playComplete();
                    toast.dismiss();
                    toast.success(t("pas_sort_complete"));
                    resetFunc();
                    return;
                }
                playSuccess();
                if (data.next_cubby && typeof data.next_cubby.location_barcode === "string") {
                    // The order's other cubby still holds stock: go straight
                    // to its tote — the closer is standing right there.
                    setUnloadSlot({
                        location_barcode: data.next_cubby.location_barcode,
                        label: String(data.next_cubby.label ?? ""),
                        order_number: unloadSlot.order_number,
                        delivery_type: unloadSlot.delivery_type,
                        bay_name: unloadSlot.bay_name,
                        cubby: data.next_cubby.slot === 1 ? 1 : 2,
                        auto: true,
                    });
                    setUnloadStep("tote");
                } else {
                    setUnloadSlot(null);
                    setUnloadStep("slot");
                }
                await refreshPlan();
                loadDemoTotes();
            } else {
                flashError();
                playError();
                toast.dismiss();
                toast.error(
                    data.reason === "tote_unavailable" ? t("pas_tote_unavailable")
                        : data.reason === "slot_already_unloaded" ? t("pas_slot_already_unloaded")
                            : data.reason === "slot_not_finished"
                                ? t("pas_slot_not_finished")
                                    .replace("{sorted}", String(data.sorted ?? "?"))
                                    .replace("{required}", String(data.required ?? "?"))
                                // slot_unknown, and the legacy slot_not_ready an
                                // un-deployed BE may still send.
                                : (data.reason === "slot_unknown" || data.reason === "slot_not_ready")
                                    ? t("pas_slot_not_ready")
                                    : t("scan_failed"),
                );
                setUnloadSlot(null);
                setUnloadStep("slot");
                refreshPlan();
            }
        } catch {
            flashError();
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
        } finally {
            setLoading(false);
        }
    }, [unloadSlot, session.wave_id, navigate, t, resetFunc, refreshPlan, loadDemoTotes]);

    // Confirm placement: the operator scans the slot's own barcode. Clears the
    // placement card and tells the BE to cut the light + recentre immediately.
    // The cycle timer is NOT stopped here — it keeps running until the next item
    // is scanned (the full cycle), which is when the bonus is awarded.
    // When this confirm is for the wave's LAST item (pendingCompleteRef), it is
    // the scan that completes the wave: only now do the completion cue, run
    // summary and complete screen fire.
    const handleConfirmLocation = useCallback(async (val: string) => {
        setPlacement(null);
        clearStatus();
        const pending = pendingCompleteRef.current;
        if (pending) {
            pendingCompleteRef.current = null;
            setAllSorted(true);
            playComplete();
            // Run stats for the completion summary: items sorted, wall-clock
            // since the first scan (through this final placement), and the
            // points banked across the wave.
            buildWaveSummary(pending.items);
            // Wave done — pull the fresh plan so every completed slot is
            // counted on the (now unlocked) unloader badge.
            refreshPlan();
        } else {
            playSuccess();
        }
        try {
            await apiFetch(`/pick-and-sort/${session.wave_id}/confirm-location`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: val }),
            });
        } catch { /* light will recentre on the 20s dwell anyway */ }
    }, [session.wave_id, refreshPlan, buildWaveSummary]);

    // Two-cubby Putwall order: the sorter found the shown cubby full, put the
    // unit in the order's OTHER cubby and scanned that one. Tell the BE to
    // route the rest of the order there (and to count this unit in that
    // cubby), then confirm placement exactly as a normal slot scan would.
    const handleSwitchSlot = useCallback(async (val: string) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/switch-slot`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: val }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json();
            if (!res.ok || !data.ok) {
                flashError();
                playError();
                toast.dismiss();
                toast.error(t("pas_slot_not_in_order"));
                return;
            }
            setAllocations(prev => prev.map(a => (
                a.order_id === data.order_id
                    ? { ...a, active_slot: data.active_slot === 2 ? 2 : 1, items_sorted_2: Number(data.items_sorted_2) || 0 }
                    : a
            )));
        } catch {
            flashError();
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
            return;
        } finally {
            setLoading(false);
        }
        await handleConfirmLocation(val);
    }, [session.wave_id, navigate, t, handleConfirmLocation]);

    // Refresh the plan on open so the modal shows counts no staler than the
    // moment it was tapped (another operator may have sorted in the poll gap).
    const openFlagModal = useCallback(() => {
        setFlagView("flag");
        setManualItems(null);
        setFlagModalOpen(true);
        refreshPlan();
    }, [refreshPlan]);

    // The per-SKU outstanding list backing the manual-sort view.
    const loadManualItems = useCallback(async () => {
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/unsorted-items`, {});
            if (!res.ok) return;
            const data = await res.json();
            setManualItems(Array.isArray(data.items) ? data.items : []);
        } catch { /* keep the last-known list */ }
    }, [session.wave_id]);

    const openManualView = useCallback(() => {
        setFlagView("manual");
        loadManualItems();
    }, [loadManualItems]);

    // Same list, for the opposite reason — the item isn't in the tote at all.
    const openMissingView = useCallback(() => {
        setMissingTarget(null);
        setFlagView("missing");
        loadManualItems();
    }, [loadManualItems]);

    // Sort one unit WITHOUT a scan — the operator tapped the item whose
    // barcode won't scan. The BE treats it exactly like a scan (same routing,
    // same response), so from here the flow is the normal one: modal closes,
    // the placement card shows, and the slot barcode must still be scanned.
    const handleManualSort = useCallback(async (sku: string) => {
        setManualBusy(true);
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/sort-item-manual`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sku }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json();
            if (res.ok && data.ok) {
                setFlagModalOpen(false);   // re-enables the scanner for the slot confirm
                applySortSuccess(data);
            } else {
                playError();
                toast.dismiss();
                toast.error(data.reason === "no_demand" ? t("pas_no_demand") : t("scan_failed"));
                loadManualItems();   // another operator may have sorted it meanwhile
            }
        } catch {
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
        } finally {
            setManualBusy(false);
        }
    }, [session.wave_id, navigate, t, applySortSuccess, loadManualItems]);

    // Write ONE item off as missing, after the operator confirmed that exact
    // item on the previous screen. The wave stays open — the rest of the tote
    // still has to be sorted — and no light is aimed, because nothing is being
    // placed: that is the whole difference between this and a manual sort.
    //
    // It can still be the last outstanding unit, in which case the BE finishes
    // the wave (nothing would ever be scanned again) and the completion screen
    // shows here, carrying the running flagged tally.
    const handleFlagItemMissing = useCallback(async () => {
        if (!missingTarget) return;
        const target = missingTarget;
        setManualBusy(true);
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/flag-item-missing`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sku: target.sku }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json();
            if (res.ok && data.ok === false && data.reason === "segment_still_picking") {
                // Paired wave: the other half may still bring this item.
                playError();
                toast.dismiss();
                toast.info(t("pas_segment_still_picking"));
                await refreshPlan();
                return;
            }
            if (!res.ok || !data.ok) {
                playError();
                toast.dismiss();
                toast.error(t("scan_failed"));
                return;
            }
            const flagged = typeof data.flagged_units === "number" ? data.flagged_units : 0;
            flaggedUnitsRef.current += flagged;
            toast.dismiss();
            if (flagged === 0) {
                // Sorted or flagged by someone else while the confirm was up.
                toast.info(t("pas_missing_item_gone"));
            } else {
                toast.success(t("pas_missing_item_success", {
                    count: String(flagged), item: target.title || target.sku,
                }));
            }
            if (data.wave_complete) {
                // Same finish as flag-remaining: read the sorted tally before
                // refreshPlan so the summary can't race the plan fetch.
                const sortedNow = totalSorted;
                setFlagModalOpen(false);
                pendingCompleteRef.current = null;
                setPlacement(null);
                timer.reset();
                setAllSorted(true);
                playComplete();
                buildWaveSummary(sortedNow, flaggedUnitsRef.current);
                await refreshPlan();
                return;
            }
            // More to sort: back to the list so a second missing item can be
            // reported without reopening the modal.
            setMissingTarget(null);
            setFlagView("missing");
            await Promise.all([refreshPlan(), loadManualItems()]);
        } catch {
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
        } finally {
            setManualBusy(false);
        }
    }, [missingTarget, session.wave_id, navigate, t, refreshPlan, loadManualItems,
        timer, totalSorted, buildWaveSummary]);

    // Write every outstanding unit off as missing. The BE pre-marks the
    // shortfall on order_items.missing_count (dispatch loads those rows already
    // resolved as missing, still packable if found — exactly like picking's
    // not-found flow) and completes the wave, so the operator goes straight on
    // to unloading.
    //
    // The run summary shows here too. Flagging is the operator reporting stock
    // that isn't there — not a failure on their part — so withholding their
    // items/time/points reads as a penalty for doing the right thing. The
    // tiles report what was actually sorted (a flagged finish simply shows a
    // lower item count than a clean one) and the flagged count sits alongside
    // them so the item total still adds up.
    const handleFlagRemaining = useCallback(async () => {
        setFlagging(true);
        try {
            const res = await apiFetch(`/pick-and-sort/${session.wave_id}/flag-remaining`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: "{}",
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json();
            if (res.ok && data.ok) {
                setFlagModalOpen(false);
                toast.dismiss();
                toast.success(t("pas_flag_success", { count: String(data.flagged_units) }));
                // Read the sorted tally BEFORE refreshPlan so the summary
                // can't race the plan fetch. The flag button is disabled while
                // a placement card is up, so there is never a pending final
                // placement to account for here.
                const sortedNow = totalSorted;
                pendingCompleteRef.current = null;
                setPlacement(null);
                timer.reset();
                setAllSorted(true);
                playComplete();
                // Items flagged one at a time earlier in the session count
                // towards the same total — the summary reports the wave's
                // shortfall, not just this button's share of it.
                buildWaveSummary(sortedNow, flaggedUnitsRef.current
                    + (typeof data.flagged_units === "number" ? data.flagged_units : 0));
                await refreshPlan();
            } else if (res.ok && data.reason === "segment_still_picking") {
                // Paired wave: nothing can be written off while the other
                // half is still being picked — the units may yet arrive.
                playError();
                toast.dismiss();
                toast.info(t("pas_segment_still_picking"));
                setFlagModalOpen(false);
                await refreshPlan();
            } else {
                playError();
                toast.dismiss();
                toast.error(t("scan_failed"));
            }
        } catch {
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
        } finally {
            setFlagging(false);
        }
    }, [session.wave_id, navigate, t, refreshPlan, timer, totalSorted, buildWaveSummary]);

    // Placement is mandatory: while a slot is showing, the operator must scan
    // that slot's own barcode to confirm. Any other scan is the wrong location —
    // flash the X + toast and keep the slot showing until the right one scans.
    const handleWrongLocation = useCallback(() => {
        flashError();
        playError();
        toast.dismiss();
        toast.error(t("wrong_location"));
    }, [t]);

    // Begin a fresh sort: confirm the consolidated source tote is at the bay by
    // scanning it. A tote belonging to a DIFFERENT queued wave isn't an error —
    // the operator brought that wave's totes to the bay, so ask the BE to
    // switch this bay onto the tote's wave (enter with a tote override). The
    // auto-entered untouched wave goes back to the queue.
    const handleSourceToteScan = useCallback(async (val: string) => {
        if (sourceTotes.length === 0 || sourceTotes.includes(val)) {
            clearStatus();
            playSuccess();
            setToteConfirmed(true);
            return;
        }
        // Any of this wave's slot barcodes anchors the bay for enter().
        const anchorBarcode = allocations[0]?.location_barcode ?? session.allocations[0]?.location_barcode;
        if (!anchorBarcode) {
            flashError(); playError(); toast.dismiss();
            toast.error(t("pas_wrong_tote"));
            return;
        }
        setLoading(true);
        try {
            const res = await apiFetch(`/pick-and-sort/enter`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: anchorBarcode, tote_barcode: val }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json();
            if (res.ok && data.ok && data.wave_id && data.wave_id !== session.wave_id) {
                playSuccess();
                onWaveChange?.(data);   // remount on the tote's wave, gate pre-confirmed
                return;
            }
            flashError(); playError(); toast.dismiss();
            toast.error(
                data.reason === "tote_not_awaiting_sort" ? t("pas_tote_no_wave")
                    : data.reason === "tote_wave_in_progress" ? `${t("pas_tote_wave_in_progress")}${Array.isArray(data.bay_names) && data.bay_names.length > 0 ? ` (${data.bay_names.join(", ")})` : ""}`
                    : data.reason === "bay_wave_in_progress" ? t("pas_bay_wave_in_progress")
                    // Large (double-bay) refusals. These say something specific
                    // and actionable — "unload the other bay", "you're at the
                    // wrong bay" — so they must not collapse into the generic
                    // wrong-tote toast the way they used to.
                    : data.reason === "large_bays_not_empty"
                        ? `${t("pas_large_bays_not_empty")}${(data.occupied_bays ?? []).length ? ` (${(data.occupied_bays ?? []).join(", ")})` : ""}`
                    : data.reason === "large_wrong_bay" ? t("pas_large_wrong_bay")
                    : data.reason === "large_bays_unavailable" ? t("pas_large_bays_unavailable")
                    : t("pas_wrong_tote"),
            );
        } catch {
            flashError(); playError(); toast.dismiss(); toast.error(t("scan_failed"));
        } finally {
            setLoading(false);
        }
    }, [sourceTotes, allocations, session.allocations, session.wave_id, onWaveChange, navigate, t]);

    // Single scan entry point — routes by view + step.
    // After a wave is fully sorted: scan a bay location to begin the next wave.
    // enter() picks the next awaiting-sort wave for the bay; if the just-sorted
    // wave is still on the bay (not unloaded) it resumes it → prompt to unload.
    const handleNextWaveScan = useCallback(async (val: string) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/pick-and-sort/enter`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: val }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json();
            if (res.ok && data.ok && data.wave_id && data.wave_id !== session.wave_id) {
                playSuccess();
                onWaveChange?.(data);   // parent swaps session → remounts on the new wave
            } else if (res.ok && data.ok) {
                flashError(); playError(); toast.dismiss();
                toast.error(t("pas_unload_first"));   // resumed the current sorted wave
            } else {
                flashError(); playError(); toast.dismiss();
                toast.error(
                    data.reason === "no_wave_awaiting_sort" ? t("pas_no_more_waves")
                        : data.reason === "nothing_to_sort" ? t("pas_nothing_to_sort")
                        : t("scan_failed"),
                );
            }
        } catch {
            flashError(); playError(); toast.dismiss(); toast.error(t("scan_failed"));
        } finally { setLoading(false); }
    }, [session.wave_id, onWaveChange, navigate, t]);

    const handleScan = useCallback((scannedValue?: string) => {
        const val = (scannedValue ?? scan).trim();
        // A scan arriving mid-request is discarded here with no feedback. It
        // should be rare (requests run ~30-70ms) — if it turns out not to be,
        // this is where the "nothing happened" scans are going.
        if (val && loading) {
            recordScanAnomaly("dropped_loading", "PickAndSort", { view }, { barcode: val });
            return;
        }
        if (!val) return;
        setScan("");
        if (view === "loading") {
            // Wave finished → a scan starts the next wave.
            if (allSorted) { handleNextWaveScan(val); return; }
            // Before sorting begins, the first scan confirms the source tote.
            if (!toteConfirmed) { handleSourceToteScan(val); return; }
            // While a slot is showing, placement is mandatory: scanning that
            // slot's barcode confirms it (light off + recentre); any other scan
            // is the wrong location and must be rejected before the next item.
            if (placement) {
                if (val === placement.barcode) handleConfirmLocation(val);
                // The order's other cubby (two-cubby Putwall order): the
                // shown cubby was full, so the unit went in here instead.
                else if (placement.partner && val === placement.partner.barcode) handleSwitchSlot(val);
                else {
                    // Rejected client-side — no request reaches the BE, so
                    // this scan is invisible server-side. Record what was
                    // scanned against what the screen was waiting for.
                    recordScanAnomaly("wrong_location", "PickAndSort",
                        { expected: placement.barcode, expected_label: placement.label },
                        { barcode: val });
                    handleWrongLocation();
                }
            } else handleSortScan(val);
        } else if (unloadStep === "slot") {
            handleSlotScan(val);
        } else if (allocations.some(a => a.location_barcode === val || a.location_2?.location_barcode === val)) {
            // A cubby barcode while a tote is expected: the closer moved on
            // (typically past an order's second cubby) — a slot scan, not a
            // tote scan, so it can't be refused as an unknown tote.
            handleSlotScan(val);
        } else {
            handleToteScan(val);
        }
    }, [scan, loading, view, allSorted, toteConfirmed, placement, unloadStep, allocations, handleNextWaveScan, handleSourceToteScan, handleConfirmLocation, handleSwitchSlot, handleWrongLocation, handleSortScan, handleSlotScan, handleToteScan]);

    // Unloader walks the bay slot-by-slot. Unload the time-critical orders first:
    // express/priority slots (A-Z by label), then standard (A-Z) — so the urgent
    // ones go out first and are less likely to get mixed in with the rest.
    // Label sort is natural (A2 before A10).
    const shipRank = (dt: string | null) => (dt === "express" || dt === "priority" ? 0 : 1);
    // Rows are cubbies (cubbyRows): a two-cubby Putwall order contributes one
    // row per cubby still holding stock without a tote.
    const sortedSlots = cubbyRows(allocations)
        .sort((a, b) =>
            shipRank(a.delivery_type) - shipRank(b.delivery_type) ||
            a.label.localeCompare(b.label, undefined, { numeric: true }));
    // Toggle badges: items still to sort in the loader, vs items sitting in
    // completed slots waiting to be unloaded. A slot becomes unloadable the moment
    // its order is fully sorted, so the unloader fills order-by-order during the
    // loading phase — no need to wait for the whole wave to finish.
    const loaderRemaining = Math.max(0, totalRequired - totalSorted);
    const unloaderItems = sortedSlots.reduce((sum, a) => sum + a.items, 0);

    // The bay being unloaded, named once at the top of the unloading view —
    // an operator arriving at the unload step otherwise has only slot labels
    // ("A3"), which repeat across bays and don't say where to stand. Resolved
    // only when the wave spans ONE bay; a Large (double-bay) wave is ambiguous
    // by nature and names each bay on its own rows and grid instead.
    const unloadBayName = useMemo(() => {
        const names = new Set<string>();
        for (const b of bayPlans) if (b.name) names.add(b.name);
        if (names.size === 0) for (const a of allocations) if (a.bay_name) names.add(a.bay_name);
        return names.size === 1 ? [...names][0] : null;
    }, [bayPlans, allocations]);

    // The bay(s) this device is working — the per-bay table marks them so the
    // closer can tell "here" from "over there". A Large wave spans two.
    const currentBayIds = useMemo(() => {
        const ids = new Set<number>();
        for (const b of bayPlans) ids.add(b.bay_id);
        if (ids.size === 0 && session.bay_id != null) ids.add(session.bay_id);
        return ids;
    }, [bayPlans, session.bay_id]);

    // Job picker — shown only to a second operator arriving at a bay that is
    // already mid-sort (the sorter's own re-entry never sees it). Both counts
    // are live: the 5s plan poll keeps running behind this screen, so a bay
    // with nothing ready yet fills in while they're looking at it.
    if (rolePick) {
        return (
            <div className="scan-panel" onMouseDown={preventScanPageButtonFocus} onClick={blurClickedScanPageButton}>
                <div className="page-title-bar">
                    <h1 className="page-title">
                        {t("pick_and_sort")}
                        {isLargeWave && <span className="pas-large-badge">{t("pas_large_wave_badge")}</span>}
                        {isPutWall && <span className="pas-large-badge">{t("pas_put_wall_badge")}</span>}
                    </h1>
                    <button className="page-back-btn" onClick={resetFunc}><ArrowLeft size={20} /></button>
                </div>
                <div className="pas-role-pick">
                    <div className="pas-role-head">
                        {unloadBayName && <div className="pas-role-bay">{unloadBayName}</div>}
                        <div className="pas-role-sorting">
                            {session.sorter_name
                                ? t("pas_role_sorting_by", { name: session.sorter_name })
                                : t("pas_role_sorting_here")}
                        </div>
                    </div>
                    <div className="pas-role-row">
                        <button type="button" className="pas-role-btn" onClick={() => setRolePick(false)}>
                            <ArrowDownToLine className="pas-role-icon" size={34} strokeWidth={2.2} aria-hidden="true" />
                            <span className="pas-role-label">{t("pas_role_sort")}</span>
                            <span className="pas-role-sub">{t("pas_role_sort_sub", { count: String(loaderRemaining) })}</span>
                        </button>
                        <button
                            type="button"
                            className="pas-role-btn pas-role-btn--unload"
                            onClick={() => { timer.reset(); setUnloadOnly(true); setView("unloading"); setRolePick(false); }}
                        >
                            <PackageOpen className="pas-role-icon" size={34} strokeWidth={2.2} aria-hidden="true" />
                            <span className="pas-role-label">{t("pas_role_unload")}</span>
                            <span className="pas-role-sub">{t("pas_role_unload_sub", { count: String(sortedSlots.length) })}</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`scan-panel${view === "unloading" ? " pas-unloading" : ""}`}
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
                <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">
                    {t("pick_and_sort")}
                    {isLargeWave && <span className="pas-large-badge">{t("pas_large_wave_badge")}</span>}
                    {isPutWall && <span className="pas-large-badge">{t("pas_put_wall_badge")}</span>}
                </h1>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {view === "loading" && <ScoreDisplay total={score.total} />}
                    {/* Missing-items escape hatch — same affordance as picking's
                        missing-item button. Disabled while a placement card is up
                        (the operator is holding an item that clearly exists) and
                        once nothing remains to flag. */}
                    {view === "loading" && toteConfirmed && !allSorted && (
                        <button
                            type="button"
                            className="picking-missing-btn picking-icon-btn"
                            onClick={openFlagModal}
                            disabled={loading || placement !== null || loaderRemaining === 0}
                            aria-label={t("pas_flag_missing_btn")}
                            title={t("pas_flag_missing_btn")}
                        >
                            <AlertTriangle size={20} />
                        </button>
                    )}
                    {/* Unload-only device (a second operator who took the
                        unloading job on a wave someone else is sorting): the
                        toggle collapses to a static badge. There is no loading
                        view to switch to, so no scan of theirs can sort an item
                        into the wave being worked next to them. */}
                    {unloadOnly ? (
                        <div className="picking-tier-toggle" role="group" aria-label={t("pas_view_unloading")}>
                            <span
                                className="picking-tier-pill pas-tier-pill picking-tier-pill--active"
                                title={t("pas_view_unloading")}
                            >
                                <PackageOpen size={16} strokeWidth={2.2} aria-hidden="true" />
                                {unloaderItems > 0 && <span className="pas-tier-badge">{unloaderItems}</span>}
                            </span>
                        </div>
                    ) : (
                    <div className="picking-tier-toggle" role="radiogroup" aria-label={t("pick_and_sort")}>
                        <button
                            type="button" role="radio" aria-checked={view === "loading"}
                            className={`picking-tier-pill pas-tier-pill${view === "loading" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => setView("loading")}
                            title={t("pas_view_loading")} aria-label={t("pas_view_loading")}
                        >
                            <ArrowDownToLine size={16} strokeWidth={2.2} aria-hidden="true" />
                            {loaderRemaining > 0 && <span className="pas-tier-badge">{loaderRemaining}</span>}
                        </button>
                        {/* Never disabled. It used to lock until a sorted slot
                            showed in local state, but that state trails the scan
                            that completes the wave by a refresh — so the tap
                            right after finishing, which is exactly when
                            operators reach for it, hit a dead button and they
                            had to leave the section and come back. The unloading
                            view refetches on entry and has its own empty state,
                            so letting the switch through is both simpler and
                            self-correcting. */}
                        <button
                            type="button" role="radio" aria-checked={view === "unloading"}
                            className={`picking-tier-pill pas-tier-pill${view === "unloading" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => { timer.reset(); setView("unloading"); }}
                            title={t("pas_view_unloading")}
                            aria-label={t("pas_view_unloading")}
                        >
                            <PackageOpen size={16} strokeWidth={2.2} aria-hidden="true" />
                            {unloaderItems > 0 && <span className="pas-tier-badge">{unloaderItems}</span>}
                        </button>
                    </div>
                    )}
                    <button className="page-back-btn" onClick={resetFunc}><ArrowLeft size={20} /></button>
                </div>
                {/* Paired wave: a half is still being picked. Its units are
                    counted in the totals but cannot be written off yet. */}
                {groupPicking && (
                    <div className="pas-segment-banner" role="status" data-testid="pas-segment-banner">
                        <Users size={18} className="pas-segment-banner-icon" aria-hidden="true" />
                        <span>
                            {(() => {
                                const live = segments?.members.find((m) => m.status !== "completed" && m.status !== "released");
                                if (!live) return t("pas_segment_picking_generic");
                                if (live.awaiting_picker || !live.picker_name) {
                                    return t("pas_segment_waiting", { no: String(live.segment_no) });
                                }
                                return t("pas_segment_picking", {
                                    no: String(live.segment_no), name: live.picker_name,
                                    done: String(live.lines_done), total: String(live.lines_total),
                                });
                            })()}
                        </span>
                    </div>
                )}
            </div>

            <TimeBonusPopup
                seconds={timeBonus}
                color={timeBonusColor}
                streak={streakApi.streak}
                isBest={isBestTime}
                bumpKey={timeBonusKeyRef.current}
            />

            <AchievementPopup milestone={achievement} bumpKey={achievementKeyRef.current} />

            <ProgressBar
                progress={timer.state.progress}
                visible={timer.state.active && view === "loading" && toteConfirmed && !allSorted}
            />

            <div className="scan-centre">
                {view === "loading" ? (
                    allSorted ? (
                        <div style={{ textAlign: "center" }}>
                            <CheckCircle strokeWidth={3} size={120} color="#16a34a" />
                            <h2 className="drop-all-caught-up" style={{ marginTop: 12 }}>{t("pas_all_sorted")}</h2>
                            {/* Run summary — only when this operator actually sorted
                                something this session (resuming someone else's wave
                                purely to finish or flag it has no run of their own to
                                report). Shown for a flagged finish too; the flagged
                                note explains the shortfall rather than the stats
                                being withheld. */}
                            {waveSummary && (
                                <>
                                    <div className="pas-complete-stats">
                                        <div className="pas-stat-tile">
                                            <Package className="pas-stat-icon" size={26} strokeWidth={2.2} aria-hidden="true" />
                                            <span className="pas-stat-value">{waveSummary.items}</span>
                                            <span className="pas-stat-label">{t("pas_complete_items")}</span>
                                        </div>
                                        <div className="pas-stat-tile">
                                            <Timer className="pas-stat-icon pas-stat-icon--time" size={26} strokeWidth={2.2} aria-hidden="true" />
                                            <span className="pas-stat-value">{formatDuration(waveSummary.elapsedMs)}</span>
                                            <span className="pas-stat-label">{t("pas_complete_time")}</span>
                                        </div>
                                        <div className="pas-stat-tile">
                                            <Gauge className="pas-stat-icon pas-stat-icon--rate" size={26} strokeWidth={2.2} aria-hidden="true" />
                                            <span className="pas-stat-value">{formatHourlyRate(waveSummary.items, waveSummary.elapsedMs)}</span>
                                            <span className="pas-stat-label">{t("pas_complete_rate")}</span>
                                        </div>
                                        <div className="pas-stat-tile">
                                            <Trophy className="pas-stat-icon pas-stat-icon--points" size={26} strokeWidth={2.2} aria-hidden="true" />
                                            <span className="pas-stat-value">{waveSummary.points}</span>
                                            <span className="pas-stat-label">{t("pas_complete_points")}</span>
                                        </div>
                                    </div>
                                    {(waveSummary.flagged ?? 0) > 0 && (
                                        <div className="pas-complete-flagged">
                                            <AlertTriangle size={16} aria-hidden="true" />
                                            {t("pas_complete_flagged", { count: String(waveSummary.flagged) })}
                                        </div>
                                    )}
                                </>
                            )}
                            {/* Sorted orders still sitting in the bay must be unloaded
                                before the slots free up for the next wave — say so,
                                rather than inviting a scan that would just error. Only
                                once there's nothing left to unload do we prompt for the
                                next wave. */}
                            {sortedSlots.length > 0 ? (
                                <div className="scan-command-sub">{t("pas_unload_first")}</div>
                            ) : queueWaiting !== null && queueWaiting > 0 ? (
                                <div className="scan-command-sub">{t("pas_more_waiting")}</div>
                            ) : null}
                        </div>
                    ) : !toteConfirmed ? (
                        // Entry gate — confirm the consolidated tote is at the bay
                        // before sorting starts, and show how many orders it holds.
                        <>
                            <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                            <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                                {t("pas_scan_source_tote").toUpperCase().split(" ").map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                            </div>
                            {waves.length > 0 && (
                                <div className="pas-wave-list">
                                    <div className="pas-wave-list-title">{t(isPutWall ? "pas_available_waves_put_wall" : "pas_available_waves_pas")}</div>
                                    {waves.map(w => (
                                        <div key={w.wave_id} className={`pas-wave-row${w.wave_id === session.wave_id ? " pas-wave-row--current" : ""}`}>
                                            <span className="pas-wave-orders">{t("pas_wave_orders", { count: String(w.order_count) })}</span>
                                            {/* A Large wave needs BOTH bays free to open. Badging it
                                                here is the only place the operator can see that before
                                                scanning — otherwise a Large wave stuck behind an
                                                occupied bay just looks like a queue that won't move. */}
                                            {w.wave_size === "large" && <span className="pas-wave-large">{t("pas_wave_large_badge")}</span>}
                                            <span className="pas-wave-meta">{w.completed_at ? new Date(w.completed_at).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                                            <span className="pas-wave-meta">{w.picker_name || "—"}</span>
                                            {w.wave_id === session.wave_id && <span className="pas-wave-now">{t("pas_now_sorting")}</span>}
                                            {w.wave_id !== session.wave_id && w.in_progress && <span className="pas-wave-busy">{t("pas_wave_in_progress")}</span>}
                                            {/* Items + source totes: one line, totes truncate with an
                                                ellipsis only when they overflow. The tote numbers let
                                                the operator match the tote in their hands to its wave. */}
                                            {typeof w.item_count === "number" && (
                                                <div className="pas-wave-sub">
                                                    <span className="pas-wave-items">{t("pas_wave_items", { count: String(w.item_count) })}</span>
                                                    {(w.totes?.length ?? 0) > 0 && (
                                                        <span className="pas-wave-totes">{t("pas_wave_totes", { totes: (w.totes ?? []).join(", ") })}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : placement ? (
                        // Slot showing (the "scan location" step). On a wrong-location
                        // scan the boxes are swapped for the centred error X — matching
                        // the rejection feedback used elsewhere on the Portal.
                        <>
                            <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                            <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                                {isLargeWave && placement.bayName && (
                                    // Double-bay wave: name WHICH bay before the slot —
                                    // the moving head lights the right one, but the
                                    // operator reads this before looking up.
                                    <div className="pas-target-bay">{placement.bayName}</div>
                                )}
                                <ZoneSorterLocationBoxes
                                    first={placement.shelf}
                                    second={placement.position}
                                    firstColour={placement.shelfColour}
                                    secondColour={placement.positionColour}
                                />
                                {placement.partner && (
                                    // Two-cubby Putwall order: the other cubby the
                                    // unit may go in once this one is full — scan
                                    // THAT cubby to confirm and the order moves there.
                                    <div className="scan-command-sub pas-partner-cubby">
                                        {t("pas_or_cubby", { label: placement.partner.label })}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                            <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                                {t("pas_scan_item").toUpperCase().split(" ").map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                            </div>
                        </>
                    )
                ) : (
                    // Unloading view
                    sortedSlots.length === 0 ? (
                        <div style={{ textAlign: "center" }}>
                            {/* Nothing ready YET and nothing ready AT ALL are
                                different situations. While the wave is still
                                being sorted the unloader is waiting on orders
                                that are coming — say so, with the sort's own
                                progress, so an empty screen doesn't read as
                                "you're done here" and send them away. */}
                            {loaderRemaining > 0 ? (
                                <>
                                    <Hourglass size={64} color="#9ca3af" />
                                    <h2 style={{ marginTop: 12 }}>{t("pas_unload_waiting")}</h2>
                                    <div className="scan-command-sub">
                                        {t("pas_progress", { sorted: String(totalSorted), total: String(totalRequired) })}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Truck size={64} color="#9ca3af" />
                                    <h2 style={{ marginTop: 12 }}>{t("pas_nothing_to_unload")}</h2>
                                </>
                            )}
                            {/* Nothing here doesn't mean nothing anywhere —
                                the per-bay table is what sends the closer to
                                the bay that does have orders waiting. */}
                            <PasBayReadyTable bays={bayReady} currentBayIds={currentBayIds} />
                        </div>
                    ) : unloadStep === "slot" ? (
                        <>
                            <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                            <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                                {t("pas_scan_slot").toUpperCase().split(" ").map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                            </div>
                            {unloadBayName && <div className="pas-unload-bay">{unloadBayName}</div>}
                            {unloadedMsg && <div style={{ marginTop: 12, fontSize: 22, fontWeight: 700, color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><CheckCircle strokeWidth={3} size={20} />{unloadedMsg}</div>}
                            <div className="scan-command-sub">{t("pas_unload_remaining", { remaining: String(sortedSlots.length) })}</div>
                            {/* More orders are still on their way into the bay —
                                without this the list looks like the whole job and
                                the unloader walks off after the last row. */}
                            {loaderRemaining > 0 && (
                                <div className="scan-command-sub">{t("pas_unload_still_sorting", { count: String(loaderRemaining) })}</div>
                            )}
                            <PasBayReadyTable bays={bayReady} currentBayIds={currentBayIds} />
                            <div className="pas-unload-list">
                                {(showAllSlots ? sortedSlots : sortedSlots.slice(0, SLOT_PREVIEW_COUNT)).map(a => (
                                    <div key={a.key} className="pas-unload-row">
                                        <span className="pas-unload-slot">
                                            {isLargeWave && a.bay_name ? `${a.bay_name} · ${a.label}` : a.label}
                                        </span>
                                        <span className="pas-unload-order">
                                            #{a.order_number}
                                            {a.two_cubby && <span className="pas-unload-cubby"> · {t("pas_cubby_of", { n: String(a.cubby) })}</span>}
                                        </span>
                                        <span className="pas-unload-count">{t("pas_unload_items", { count: String(a.items) })}</span>
                                    </div>
                                ))}
                                {sortedSlots.length > SLOT_PREVIEW_COUNT && (
                                    <button type="button" className="pas-unload-more" onClick={() => setShowAllSlots(v => !v)}>
                                        {showAllSlots
                                            ? t("pas_unload_see_less")
                                            : t("pas_unload_load_more", { count: String(sortedSlots.length - SLOT_PREVIEW_COUNT) })}
                                    </button>
                                )}
                            </div>
                            {bayPlans.length > 1 ? (
                                // Double-bay wave: the bays are stacked in physical
                                // order (buildPlan sorts them by bay position), each
                                // as its own titled panel with its own outstanding
                                // count, so two identical-looking grids can't be
                                // mistaken for one another. Each bay's toEmpty set is
                                // built from ITS OWN allocations, so the same label
                                // ("A1") in the other bay can't falsely highlight here.
                                <div className="pas-bay-stack">
                                    {bayPlans.map(bay => {
                                        const bayRemaining = sortedSlots.filter(a => a.bay_id === bay.bay_id);
                                        return (
                                            <PickAndSortUnloadGrid
                                                key={bay.bay_id}
                                                slots={bay.slots}
                                                toEmpty={new Set(bayRemaining.map(a => a.label))}
                                                title={bay.name ?? undefined}
                                                statusLabel={bayRemaining.length === 0
                                                    ? t("pas_bay_clear")
                                                    : t("pas_bay_to_empty", { count: String(bayRemaining.length) })}
                                            />
                                        );
                                    })}
                                </div>
                            ) : (
                                <PickAndSortUnloadGrid slots={baySlots} toEmpty={new Set(sortedSlots.map(a => a.label))} title={unloadBayName ?? undefined} />
                            )}
                        </>
                    ) : (
                        <>
                            <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                            <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                                {(unloadSlot?.auto
                                    ? t("pas_scan_tote_next_cubby", { label: unloadSlot.label })
                                    : t("pas_scan_tote")
                                ).toUpperCase().split(" ").map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 12 }}>#{unloadSlot?.order_number}</div>
                            {unloadSlot?.cubby && (
                                // Two-cubby order: say which cubby's tote this is,
                                // so "two totes for one order" never surprises.
                                <div className="scan-command-sub pas-unload-cubby">{t("pas_cubby_of", { n: String(unloadSlot.cubby) })}</div>
                            )}
                            {(() => {
                                // The order's shipping method. Standard stays black and
                                // low-key; Express / Priority go gold (and share one icon)
                                // so the operator can't miss a time-critical order before
                                // it goes in the tote.
                                const dt = (unloadSlot?.delivery_type ?? "").toLowerCase();
                                if (dt !== "standard" && dt !== "express" && dt !== "priority" && dt !== "collection") return null;
                                // Collection = counter pick-up: its own icon, not gold.
                                const Icon = dt === "standard" ? Truck : dt === "collection" ? Store : Zap;
                                return (
                                    <div className={`pas-ship pas-ship--${dt}`}>
                                        <Icon size={34} strokeWidth={2.4} aria-hidden="true" />
                                        {t(`delivery_type_${dt}`)}
                                    </div>
                                );
                            })()}
                            {/* Name the bay alongside the slot: labels like "A3"
                                repeat across bays, so the slot alone doesn't say
                                where to go. */}
                            <div className="scan-command-sub">
                                {unloadSlot?.bay_name ? `${unloadSlot.bay_name} · ${unloadSlot.label}` : unloadSlot?.label}
                            </div>
                        </>
                    )
                )}
                {view === "loading" && !allSorted && toteConfirmed && !placement && (
                    <div className="scan-command-sub">
                        {t("pas_progress", { sorted: String(totalSorted), total: String(totalRequired) })}
                    </div>
                )}
            </div>

            {/* Scanning stays live when sorted so a bay-location scan starts the
                next wave — but not behind the flag modal, whose buttons are the
                only valid interaction while it's open. */}
            <HiddenScanInput value={scan} onChange={setScan} onSubmit={handleScan} disabled={loading || flagModalOpen} disableCamera telemetryModule="PickAndSort" />

            {/* Demo: once the wave's sorted AND unloaded, scan a bay location to
                begin the next wave. While orders still need unloading, the unloading
                tab's demo buttons drive that step instead. */}
            {demo && view === "loading" && allSorted && sortedSlots.length === 0 && queueWaiting !== null && queueWaiting > 0 && allocations[0] && (
                <div className="demo-scan-panel">
                    <span className="demo-scan-label">Demo · Next wave</span>
                    <button className="demo-scan-btn" onClick={() => handleScan(allocations[0].location_barcode)} disabled={loading}>{allocations[0].location_barcode}</button>
                </div>
            )}

            {/* Demo: entry gate — scan the wave's source tote to begin sorting. */}
            {demo && view === "loading" && !allSorted && !toteConfirmed && sourceTotes.length > 0 && (
                <div className="demo-scan-panel">
                    <span className="demo-scan-label">Demo · Tote</span>
                    <button className="demo-scan-btn demo-scan-btn--tote" onClick={() => handleScan(sourceTotes[0])} disabled={loading}>{sourceTotes[0]}</button>
                </div>
            )}

            {/* Demo affordances — walk the real two-step flow one scan at a time:
                a single item to sort, then that slot's location to confirm. */}
            {demo && view === "loading" && !allSorted && toteConfirmed && (placement || demoItems.length > 0) && (
                <div className="demo-scan-panel">
                    {placement ? (
                        <>
                            <span className="demo-scan-label">Demo · Location</span>
                            <button className="demo-scan-btn" onClick={() => handleScan(placement.barcode)} disabled={loading}>{placement.label}</button>
                        </>
                    ) : (
                        <>
                            <span className="demo-scan-label">Demo · Item</span>
                            <button className="demo-scan-btn" onClick={() => handleScan(demoItems[0])} disabled={loading}>{demoItems[0]}</button>
                        </>
                    )}
                </div>
            )}
            {demo && view === "unloading" && (sortedSlots.length > 0) && (
                <div className="demo-scan-panel">
                    {unloadStep === "slot" ? (
                        <>
                            <span className="demo-scan-label">Demo · Slots</span>
                            {sortedSlots.map(a => (
                                <button key={a.allocation_id} className="demo-scan-btn" onClick={() => handleScan(a.location_barcode)} disabled={loading}>{a.label}</button>
                            ))}
                        </>
                    ) : (
                        <>
                            <span className="demo-scan-label">Demo · Totes</span>
                            {demoTotes.map(tote => (
                                <button key={tote} className="demo-scan-btn demo-scan-btn--tote" onClick={() => handleScan(tote)} disabled={loading}>{tote}</button>
                            ))}
                        </>
                    )}
                </div>
            )}

            {/* Flag-remaining-missing confirmation. Full-screen on mobile. The
                rows are per-slot (what the operator can physically verify at the
                bay): slot label, order number, units outstanding. */}
            {flagModalOpen && (() => {
                const shortfalls = allocations
                    .filter(a => (a.status === "allocated" || a.status === "sorting") && a.items_sorted < a.items_required)
                    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
                // The outstanding-items list, shared by the two views that pick
                // an item out of it (manual sort, missing). Identical rows on
                // purpose — it's the same physical question, "which item?" —
                // so only the hint above and what the tap does differ.
                const renderItemList = (onPick: (it: UnsortedItem) => void) => (
                    <div className="pas-manual-list">
                        {manualItems === null ? (
                            <div className="pas-manual-empty">{t("loading")}</div>
                        ) : manualItems.length === 0 ? (
                            <div className="pas-manual-empty">{t("pas_manual_empty")}</div>
                        ) : (
                            manualItems.map(it => (
                                <button
                                    key={it.sku}
                                    type="button"
                                    className="pas-manual-row"
                                    onClick={() => onPick(it)}
                                    disabled={manualBusy}
                                >
                                    {/* Thumbnail helps match the physical item by eye. The
                                        Package icon fills in when there's no image; a broken
                                        URL just hides, leaving the neutral square. */}
                                    <span className="pas-manual-thumb" aria-hidden="true">
                                        {it.image ? (
                                            <img
                                                src={it.image}
                                                alt=""
                                                loading="lazy"
                                                onError={e => { e.currentTarget.style.display = "none"; }}
                                            />
                                        ) : (
                                            <Package size={22} />
                                        )}
                                    </span>
                                    <span className="pas-manual-info">
                                        <span className="pas-manual-title">{it.title || it.sku}</span>
                                        <span className="pas-manual-sku">{it.sku}</span>
                                    </span>
                                    <span className="pas-manual-count">
                                        {t("pas_manual_remaining", { count: String(it.remaining) })}
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                );
                return (
                    <div className="modal-overlay pas-flag-overlay" onClick={() => { if (!flagging && !manualBusy) setFlagModalOpen(false); }}>
                        <div className="modal-card pas-flag-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-card-header">
                                <h2>{t(
                                    flagView === "manual" ? "pas_manual_title"
                                        : flagView === "missing" ? "pas_missing_item_title"
                                            : flagView === "missing-confirm" && missingTarget ? "pas_missing_item_confirm_title"
                                                : "pas_flag_missing_title",
                                )}</h2>
                                <button className="modal-close" onClick={() => setFlagModalOpen(false)} disabled={flagging || manualBusy} aria-label={t("cancel")}>
                                    <X size={18} />
                                </button>
                            </div>
                            {flagView === "manual" ? (
                                // "Barcode not scanning" — the item exists but won't
                                // scan. Tapping it sorts one unit exactly as a scan
                                // would; the slot confirm afterwards is unchanged.
                                <div className="modal-card-body">
                                    <div className="pas-flag-question">{t("pas_manual_hint")}</div>
                                    {renderItemList(it => handleManualSort(it.sku))}
                                    <div className="pas-flag-actions">
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-lg btn-full"
                                            onClick={() => setFlagView("flag")}
                                            disabled={manualBusy}
                                        >
                                            {t("back")}
                                        </button>
                                    </div>
                                </div>
                            ) : flagView === "missing" ? (
                                // The item isn't in the tote at all. Tapping it only
                                // SELECTS it — the write-off happens on the confirm
                                // screen, so a mis-tap in a list of lookalike
                                // thumbnails can't silently write stock off.
                                <div className="modal-card-body">
                                    <div className="pas-flag-question">{t("pas_missing_item_hint")}</div>
                                    {renderItemList(it => { setMissingTarget(it); setFlagView("missing-confirm"); })}
                                    <div className="pas-flag-actions">
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-lg btn-full"
                                            onClick={() => setFlagView("flag")}
                                            disabled={manualBusy}
                                        >
                                            {t("back")}
                                        </button>
                                    </div>
                                </div>
                            ) : flagView === "missing-confirm" && missingTarget ? (
                                // The double confirmation: one item, named and
                                // pictured, with what flagging it will do spelled
                                // out. Nothing has been written off yet.
                                <div className="modal-card-body">
                                    <div className="pas-missing-confirm-item">
                                        <span className="pas-manual-thumb" aria-hidden="true">
                                            {missingTarget.image ? (
                                                <img
                                                    src={missingTarget.image}
                                                    alt=""
                                                    loading="lazy"
                                                    onError={e => { e.currentTarget.style.display = "none"; }}
                                                />
                                            ) : (
                                                <Package size={22} />
                                            )}
                                        </span>
                                        <span className="pas-manual-info">
                                            <span className="pas-manual-title">{missingTarget.title || missingTarget.sku}</span>
                                            <span className="pas-manual-sku">{missingTarget.sku}</span>
                                        </span>
                                    </div>
                                    <div className="pas-flag-question">
                                        {t("pas_missing_item_confirm_q", {
                                            count: String(missingTarget.remaining),
                                            item: missingTarget.title || missingTarget.sku,
                                        })}
                                    </div>
                                    <div className="pas-flag-zero-note">{t("pas_missing_item_confirm_note")}</div>
                                    <div className="pas-flag-actions">
                                        <button
                                            type="button"
                                            className="btn btn-lg btn-full pas-flag-confirm-btn"
                                            onClick={handleFlagItemMissing}
                                            disabled={manualBusy}
                                        >
                                            <AlertTriangle size={18} />
                                            {t("pas_missing_item_confirm_btn", { count: String(missingTarget.remaining) })}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-lg btn-full"
                                            onClick={() => { setMissingTarget(null); setFlagView("missing"); }}
                                            disabled={manualBusy}
                                        >
                                            {t("back")}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="modal-card-body">
                                    <div className="pas-flag-progress">
                                        {t("pas_flag_missing_summary", { sorted: String(totalSorted), total: String(totalRequired) })}
                                    </div>
                                    <div className="pas-flag-question">
                                        {t("pas_flag_missing_question", { count: String(loaderRemaining) })}
                                    </div>
                                    {shortfalls.length > 0 && (
                                        <div className="pas-flag-list">
                                            {shortfalls.map(a => (
                                                <div key={a.allocation_id} className="pas-flag-row">
                                                    <span className="pas-flag-slot">
                                                        {isLargeWave && a.bay_name ? `${a.bay_name} · ${a.label}` : a.label}
                                                    </span>
                                                    <span className="pas-flag-order">#{a.order_number}</span>
                                                    <span className="pas-flag-qty">
                                                        {t("pas_flag_missing_qty", { count: String(a.items_required - a.items_sorted) })}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {shortfalls.some(a => a.items_sorted === 0) && (
                                        <div className="pas-flag-zero-note">{t("pas_flag_missing_zero_note")}</div>
                                    )}
                                    <div className="pas-flag-actions">
                                        <button
                                            type="button"
                                            className="btn btn-lg btn-full pas-manual-btn"
                                            onClick={openManualView}
                                            disabled={flagging}
                                        >
                                            <ScanBarcode size={18} />
                                            {t("pas_manual_btn")}
                                        </button>
                                        {/* One item missing, the rest of the tote fine —
                                            the common case, and the reason this modal was
                                            being reached through the manual-sort list. */}
                                        <button
                                            type="button"
                                            className="btn btn-lg btn-full pas-missing-item-btn"
                                            onClick={openMissingView}
                                            disabled={flagging}
                                        >
                                            <PackageX size={18} />
                                            {t("pas_missing_item_btn")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-lg btn-full pas-flag-confirm-btn"
                                            onClick={handleFlagRemaining}
                                            disabled={flagging}
                                        >
                                            <AlertTriangle size={18} />
                                            {t("pas_flag_missing_confirm", { count: String(loaderRemaining) })}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-lg btn-full"
                                            onClick={() => setFlagModalOpen(false)}
                                            disabled={flagging}
                                        >
                                            {t("cancel")}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default ZoneStoragePickAndSort;
